import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { Download, Globe, AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/common/DataTable';
import { ComboInput } from '@/components/common/ComboInput';
import { DateRangePicker, DateRange } from '@/components/common/DateRangePicker';
import { useHeader } from '@/components/context/HeaderContext';
import { fetchPostHogGuestUsers, IGuestUserReport } from '@/store/api/posthogAnalytics';

const getDefault7DaysRange = (): DateRange => {
  const from = new Date();
  from.setDate(from.getDate() - 6);
  from.setHours(0, 0, 0, 0);
  const to = new Date();
  to.setHours(23, 59, 59, 999);
  return { from, to };
};

export default function GuestUsersPage() {
  const { setHeader } = useHeader();

  const [guestUsers, setGuestUsers] = useState<IGuestUserReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [page, setPage] = useState(1);
  const [take, setTake] = useState(10);
  const [search, setSearch] = useState('');
  const [guestDateRange, setGuestDateRange] = useState<DateRange>(getDefault7DaysRange());
  const [guestLimit, setGuestLimit] = useState(100);

  const loadData = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchPostHogGuestUsers(guestDateRange, guestLimit)
      .then((data) => {
        setGuestUsers(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setError('Failed to fetch guest users from telemetry database.');
        setLoading(false);
      });
  }, [guestDateRange, guestLimit]);

  useEffect(() => {
    setHeader({
      type: 'user',
      props: {
        title: 'Guest Users',
        lastUpdated: new Date().toLocaleString(),
        showSort: false,
        showFilter: false,
        showMore: false,
      },
    });
    return () => setHeader(null);
  }, [setHeader]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const handleTakeChange = useCallback((val: number) => {
    setTake(val);
    setPage(1);
  }, []);

  // Filtering guest users based on search
  const filteredGuests = useMemo(() => {
    return guestUsers.filter(
      (guest) =>
        guest.ip.toLowerCase().includes(search.toLowerCase().trim()) ||
        guest.city.toLowerCase().includes(search.toLowerCase().trim()) ||
        guest.state.toLowerCase().includes(search.toLowerCase().trim()) ||
        guest.country.toLowerCase().includes(search.toLowerCase().trim())
    );
  }, [guestUsers, search]);

  const guestsWithSNo = useMemo(() => {
    return filteredGuests.map((guest, index) => ({
      ...guest,
      sNo: index + 1,
    }));
  }, [filteredGuests]);

  const handleExportCSV = () => {
    if (guestUsers.length === 0) return;

    const headers = ['S.No', 'IP Address', 'City', 'State', 'Country', 'Visited At'];
    const rows = guestUsers.map((guest, idx) => [
      idx + 1,
      guest.ip,
      `"${guest.city.replace(/"/g, '""')}"`,
      `"${guest.state.replace(/"/g, '""')}"`,
      `"${guest.country.replace(/"/g, '""')}"`,
      `"${new Date(guest.timestamp).toLocaleString()}"`,
    ]);

    const csvContent =
      'data:text/csv;charset=utf-8,' + [headers, ...rows].map((e) => e.join(',')).join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `guest_users_report.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns = useMemo<ColumnDef<any>[]>(
    () => [
      {
        accessorKey: 'sNo',
        header: 'S.No',
        cell: ({ row }) => <div className="text-[#1E0036] font-medium">{row.getValue('sNo')}</div>,
      },
      {
        accessorKey: 'ip',
        header: 'IP Address',
        cell: ({ row }) => (
          <span className="font-mono text-xs text-[#1E0036]">{row.getValue('ip')}</span>
        ),
      },
      {
        accessorKey: 'city',
        header: 'City',
        cell: ({ row }) => (
          <span className="font-semibold text-[#1E0036]">{row.getValue('city')}</span>
        ),
      },
      {
        accessorKey: 'state',
        header: 'State',
        cell: ({ row }) => <span className="text-[#62556B]">{row.getValue('state')}</span>,
      },
      {
        accessorKey: 'country',
        header: 'Country',
        cell: ({ row }) => (
          <div className="flex items-center gap-1.5 text-sm text-[#1E0036]">
            <Globe className="w-4 h-4 text-violet-400 shrink-0" />
            <span>{row.getValue('country')}</span>
          </div>
        ),
      },
      {
        accessorKey: 'timestamp',
        header: 'Visited At',
        cell: ({ row }) => {
          const dateStr = row.getValue('timestamp') as string;
          return (
            <span className="text-sm text-[#62556B]">
              {dateStr ? new Date(dateStr).toLocaleString() : 'N/A'}
            </span>
          );
        },
      },
    ],
    []
  );

  return (
    <div className="p-3 sm:p-8 min-h-screen space-y-6 font-noto">
      <div className="flex flex-col sm:flex-row justify-between items-center gap-2">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-[#1E0036]">Visitors</h1>
          <p className="text-[#8E87A4] text-sm mt-1">
            List of anonymous visitor hits captured on the login/landing page.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={loadData}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-[#E5E7EB] bg-white text-[#1E0036] hover:bg-gray-50 transition-colors"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
          <Button
            onClick={handleExportCSV}
            disabled={guestUsers.length === 0}
            className="bg-[#1E0036] hover:bg-lightPurple text-white px-5 h-10 rounded-lg transition-all flex items-center gap-2 cursor-pointer disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </Button>
        </div>
      </div>

      {error ? (
        <div className="bg-red-50 border border-red-200/50 p-6 rounded-[20px] flex items-center gap-3 text-red-700 text-sm">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <span>{error}</span>
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={guestsWithSNo}
          title="Guest User Visits"
          totalCount={guestsWithSNo.length}
          page={page}
          take={take}
          loading={loading}
          search={search}
          onPageChange={setPage}
          onTakeChange={handleTakeChange}
          enableSearch
          searchPlaceholder="Search by IP, city, state, or country..."
          onSearchChange={handleSearchChange}
          extraActions={
            <div className="flex items-center gap-2 mr-2">
              <DateRangePicker
                value={guestDateRange}
                onChange={setGuestDateRange}
                className="w-[210px]"
                align="right"
              />
              <ComboInput 
                value={guestLimit} 
                onChange={setGuestLimit} 
                options={[10, 50, 100, 500, 1000]} 
              />
            </div>
          }
        />
      )}
    </div>
  );
}
